import React, { useState, useEffect, useRef, useCallback } from "react";
import * as echarts from "echarts";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSkull, faHeartbeat, faTruckMedical, faHouseFloodWater,
  faInfoCircle, faCity,
} from "@fortawesome/free-solid-svg-icons";
import SvgThunderstorm from "../../../../Components/IconComponents/Thunderstorm";
import LMSButton from "../../Common/LMSButton/LMSButton";
import { getData, postDataWithBody } from "../../../../Services/Services";
import {
  cloudburstcardsdata, cloudburstchartdata, cloudburstpopupdata,
} from "../../../../Services/EndPoints";
import "react-datepicker/dist/react-datepicker.css";
import "./CloudBrustDashboard.css";

// ── Palette (storm/cloudburst) ───────────────────────────────────────────
const C = {
  bg: "#F0F9FF",
  surface: "#FFFFFF",
  navBg: "#0C4A6E",
  primary: "#0284C7",
  text1: "#0C4A6E",
  text2: "#1D5F8A",
  text3: "#7BA5BE",
  border: "rgba(2,132,199,.10)",
};
// ── Pastel icon palette (matching Avalanche) ──────────────────────────
const PALETTE = [
  { bg: "#FFF0F0", color: "#D95555" },
  { bg: "#FFF4EA", color: "#D4772A" },
  { bg: "#EEFAF3", color: "#27A05A" },
  { bg: "#EBF4FF", color: "#3579D4" },
];

// ── Card Config ──────────────────────────────────────────────────────────
const CARDS = [
  { key: "no_of_deaths_today",                    label: "Deaths" },
  { key: "no_of_persons_hospitalized_today",       label: "Hospitalized Today" },
  { key: "no_of_persons_hospitalized_pay_done_today", label: "Hospitalized Payments" },
  { key: "no_of_dec_ex_gratia_pay_done_today",     label: "Ex-Gratia Payments" },
];

const formatLabel = (key) => ({
  no_of_deaths_today: "Deaths",
  no_of_persons_hospitalized_today: "Hospitalized",
  no_of_persons_hospitalized_pay_done_today: "Medical Payments",
  no_of_dec_ex_gratia_pay_done_today: "Ex-Gratia Payments",
}[key] || key);

const getCardColor = (key) => ({
  no_of_deaths_today: "#1C4E72",
  no_of_persons_hospitalized_today: "#1D6FA4",
  no_of_persons_hospitalized_pay_done_today: "#0284C7",
  no_of_dec_ex_gratia_pay_done_today: "#3A85B5",
}[key] || C.primary);

// ── SVG Icons (matching Avalanche icon style) ────────────────────────────
function CloudburstIcon({ keyName = "", label = "", color = "#0284C7" }) {
  const k = (keyName + " " + label).toLowerCase();
  const sw = "1.55";
  const r = "round";

  if (k.includes("death") || k.includes("fatal") || k.includes("loss"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <circle cx="10" cy="6.5" r="2.8" stroke={color} strokeWidth={sw}/>
        <path d="M4.5 18c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke={color} strokeWidth={sw} strokeLinecap={r}/>
        <path d="M7.8 5l4.4 4.4M12.2 5l-4.4 4.4" stroke={color} strokeWidth="1.4" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && !k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("hospital") && k.includes("pay"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <rect x="3" y="3" width="14" height="14" rx="4" stroke={color} strokeWidth={sw}/>
        <path d="M10 7v6M7 10h6" stroke={color} strokeWidth="1.8" strokeLinecap={r}/>
      </svg>
    );

  if (k.includes("gratia") || k.includes("compens"))
    return (
      <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
        <path d="M10 2.5L3.5 5v5.5c0 4 2.9 6.5 6.5 7 3.6-.5 6.5-3 6.5-7V5L10 2.5z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/>
        <path d="M7.5 10l2 2 3-3.5" stroke={color} strokeWidth="1.5" strokeLinecap={r} strokeLinejoin={r}/>
      </svg>
    );

  return (
    <svg viewBox="0 0 20 20" fill="none" width="17" height="17">
      <path d="M14 8a4 4 0 00-7.3-2 3 3 0 10-.7 5.8" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity=".9"/>
      <path d="M7 12l-1 4M10 12l-1 4M13 12l-1 4" stroke={color} strokeWidth="1.3" strokeLinecap="round" opacity=".8"/>
    </svg>
  );
}

// ── Popup ────────────────────────────────────────────────────────────────
const CloudBurstPopup = ({ show, onHide, districtData, incidentType, blocks }) => {
  if (!show) return null;
  return (
    <div className="cbd-popup-overlay" onClick={onHide}>
      <div className="cbd-popup" onClick={e => e.stopPropagation()}>
        <div className="cbd-popup-header">
          <div className="cbd-popup-header-left">
            <div className="cbd-popup-logo"><SvgThunderstorm width="18" height="18" style={{ fill: 'white' }} /></div>
            <div style={{ minWidth: 0 }}>
              <div className="cbd-popup-overline">Cloud Burst Zone</div>
              <div className="cbd-popup-title-text">{districtData} • {formatLabel(incidentType)}</div>
            </div>
          </div>
          <button className="cbd-popup-close" onClick={onHide}>✕</button>
        </div>
        <div className="cbd-popup-body">
          {blocks?.length > 0 ? blocks.map((block, i) => (
            <div key={i} className="cbd-popup-row">
              <div className="cbd-popup-row-left">
                <div className="cbd-popup-row-num">{i + 1}</div>
                <FontAwesomeIcon icon={faCity} style={{ color: C.primary, fontSize: 12 }} />
                <span>{block.block_name}</span>
              </div>
              <div className="cbd-popup-row-badge">{block.count.toLocaleString("en-IN")}</div>
            </div>
          )) : (
            <div className="cbd-popup-empty">
              <FontAwesomeIcon icon={faInfoCircle} className="cbd-popup-empty-icon" />
              <div className="cbd-popup-empty-text">No Block Data Available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function CloudBurstDashboard() {
  const handle = useFullScreenHandle();
  const [loader, setLoader]               = useState(false);
  const [error, setError]                 = useState(null);
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [cardData, setCardData]           = useState({});
  const [selectedIncident, setSelectedIncident] = useState("");
  const [popupState, setPopupState]       = useState({ show: false, district: null, blocks: [], incidentType: null });
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);

  const [internalFromDate, setInternalFromDate] = useState(new Date(new Date().getFullYear(), 0, 1));
  const [internalToDate,   setInternalToDate]   = useState(new Date());
  const [appliedFromDate,  setAppliedFromDate]  = useState(new Date(new Date().getFullYear(), 0, 1));
  const [appliedToDate,    setAppliedToDate]    = useState(new Date());

  const startDateStr = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const endDateStr   = appliedToDate   ? moment(appliedToDate).format("YYYY-MM-DD")   : "";

  const fetchCardData = useCallback(async (fromDate, toDate) => {
    const s = moment(fromDate).format("YYYY-MM-DD");
    const e = moment(toDate).format("YYYY-MM-DD");
    setLoader(true); setError(null);
    try {
      const data = await getData(cloudburstcardsdata.replace("FROMDATE", s).replace("TODATE", e));
      setCardData(data?.[0] || {});
    } catch { setError("Failed to load data."); }
    finally { setLoader(false); }
  }, []);

  useEffect(() => { fetchCardData(appliedFromDate, appliedToDate); }, []); // eslint-disable-line

  const handleApply = () => {
    if (!internalFromDate || !internalToDate) { setError("Please select both From and To dates"); return; }
    if (internalFromDate > internalToDate)    { setError("From date cannot be greater than To date"); return; }
    setError(null);
    setAppliedFromDate(internalFromDate);
    setAppliedToDate(internalToDate);
    fetchCardData(internalFromDate, internalToDate);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const newFrom = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const nextTo = today <= yearEnd ? today : yearEnd;
    setInternalFromDate(newFrom);
    setInternalToDate(nextTo);
    setAppliedFromDate(newFrom);
    setAppliedToDate(nextTo);
    fetchCardData(newFrom, nextTo);
  };
  const ReadOnlyDateInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} onKeyDown={e => e.preventDefault()} readOnly />
));

  useEffect(() => {
    if (internalFromDate && internalToDate && internalFromDate > internalToDate)
      setError("Select proper From Date and To Date");
    else setError(null);
  }, [internalFromDate, internalToDate]);

  const handleBarClick = async (district) => {
    try {
      const regionIds = district.region_id.split(",").map(Number);
      const blockData = await postDataWithBody(cloudburstpopupdata, {
        region_id_list: regionIds, from_date: appliedFromDate,
        to_date: appliedToDate, bar_type: selectedIncident,
      }, {});
      setPopupState({ show: true, district: district.dist_name, blocks: blockData || [], incidentType: selectedIncident });
    } catch {
      setPopupState({ show: true, district: district.dist_name, blocks: [], incidentType: selectedIncident });
    }
  };

  const isApplyDisabled = !!error || !internalFromDate || !internalToDate;

  return (
    <FullScreen handle={handle}>
      <div className={`cbd-root${isFullscreen ? " cbd-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="cbd-nav">
          <div className="cbd-nav-brand">
            <div className="cbd-nav-logo">
              <SvgThunderstorm width="18" height="18" style={{ fill: 'white' }} />
            </div>
            <div className="cbd-nav-title">Cloud Burst Dashboard</div>
          </div>

          <div className="cbd-nav-right">
            <div className="cbd-nav-group">
              <span className="cbd-nav-label">Year</span>
              <select className="cbd-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>

            <div className="cbd-daterange">
              <div className="cbd-daterange-field">
                <span className="cbd-dr-label">FROM</span>
                <ReactDatePicker
                  selected={internalFromDate}
                  onChange={date => setInternalFromDate(date)}
                  onKeyDown={e => e.preventDefault()}
                  dateFormat="dd/MM/yyyy"
                  isClearable placeholderText="Select date" todayButton="Today"
                  className="cbd-dp-input" clearButtonClassName="cbd-dp-clear"
                  customInput={<ReadOnlyDateInput />}
                />
              </div>
              <div className="cbd-dr-divider" />
              <div className="cbd-daterange-field">
                <span className="cbd-dr-label">TO</span>
                <ReactDatePicker
                  selected={internalToDate}
                  onChange={date => setInternalToDate(date)}
                  onKeyDown={e => e.preventDefault()}
                  dateFormat="dd/MM/yyyy"
                  isClearable placeholderText="Select date" todayButton="Today"
                  className="cbd-dp-input" clearButtonClassName="cbd-dp-clear"
                  customInput={<ReadOnlyDateInput />}
                />
              </div>
            </div>

            <button className="cbd-btn-apply" onClick={handleApply} disabled={isApplyDisabled}>
              Apply
            </button>

            <button className="cbd-btn-icon" onClick={() => { isFullscreen ? handle.exit() : handle.enter(); setIsFullscreen(!isFullscreen); }}>
              {isFullscreen ? (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14">
                  <path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              )}
            </button>
          </div>
        </nav>

        {error && (
          <div className="cbd-error-strip">
            <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="cbd-content">

          {/* ── HORIZONTAL CARDS ROW ── */}
          <div className="cbd-cards-row">
            <div className="cbd-cards-inner">
              {CARDS.map((cfg, idx) => {
                const p = PALETTE[idx % PALETTE.length];
                const active = selectedIncident === cfg.key;
                return (
                  <div
                    key={cfg.key}
                    className={`cbd-card-item${active ? " cbd-card-item--active" : ""}`}
                    onClick={() => setSelectedIncident(cfg.key)}
                  >
                    <span className="cbd-card-icon" style={{ background: p.bg }}>
                      <CloudburstIcon keyName={cfg.key} label={cfg.label} color={p.color} />
                    </span>
                    <span className="cbd-card-text">
                      <span className="cbd-card-label">{cfg.label}</span>
                      <span className="cbd-card-value">
                        {cardData[cfg.key] != null ? cardData[cfg.key].toLocaleString("en-IN") : "0"}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── BAR CHART AREA ── */}
          <div className={`cbd-chart-wrap${isTrendExpanded ? " cbd-chart-wrap--expanded" : ""}`}>
            <div className="cbd-chart-top">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div>
                  <h3 className="cbd-chart-heading">
                  {selectedIncident ? formatLabel(selectedIncident) : "Incident Trend Overview"}
                </h3>
                {!selectedIncident && (
                  <p className="cbd-chart-range">Select any incident from the cards above to view its trend</p>
                )}
                {selectedIncident && (
                  <p className="cbd-chart-range">
                    {moment(startDateStr).format("DD/MM/YYYY")} — {moment(endDateStr).format("DD/MM/YYYY")}
                  </p>
                )}
              </div>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {selectedIncident && (
                <button className="cbd-badge-btn" onClick={() => setSelectedIncident("")}>
                  {formatLabel(selectedIncident)} ✕
                </button>
              )}
              
                {!isTrendExpanded && (
                  <button 
                    className="cbd-btn-icon" 
                    style={{ background: "rgba(2,132,199,.08)", border: "1px solid rgba(2,132,199,.12)", color: C.primary }}
                    onClick={() => setIsTrendMinimized(!isTrendMinimized)}
                    title={isTrendMinimized ? "Expand Card" : "Collapse Card"}
                  >
                    {isTrendMinimized ? (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    ) : (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    )}
                  </button>
                )}

                <button 
                  className="cbd-btn-icon" 
                  style={{ background: "rgba(2,132,199,.08)", border: "1px solid rgba(2,132,199,.12)", color: C.primary }}
                  onClick={() => setIsTrendExpanded(!isTrendExpanded)}
                  title={isTrendExpanded ? "Collapse Fullscreen" : "Expand Fullscreen"}
                >
                  {isTrendExpanded ? (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M13 7H9V3M9 7L15 1M7 13V9H3M7 9L1 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M10 1H15V6M15 1L9 7M6 15H1V10M1 15L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            </div>

            {(isTrendExpanded || !isTrendMinimized) && (
              <>

            {!selectedIncident && (
              <div className="cbd-chart-empty">
                <div className="cbd-chart-empty-icon">
                  <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
                    <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/>
                    <rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/>
                    <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/>
                    <rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/>
                    <path d="M4 8h40" stroke="#C5DFF0" strokeWidth="1.5" strokeDasharray="3 3"/>
                  </svg>
                </div>
                <p className="cbd-chart-empty-text">Click any incident card above to visualize its trend</p>
              </div>
            )}

            <div className="cbd-chart-area">
              <CloudBurstBarChart
                selectedIncident={selectedIncident}
                startDateStr={startDateStr}
                endDateStr={endDateStr}
                onBarClick={handleBarClick}
                isTrendExpanded={isTrendExpanded}
              />
            </div>
            </>
            )}
          </div>

          {/* ── BOTTOM 3 CHARTS ── */}
          <div className="cbd-bottom-charts">
            <PictorialBarChart cardData={cardData} />
            <NightingaleRoseChart cardData={cardData} />
            <ScatterChart cardData={cardData} />
          </div>

        </div>

        <CloudBurstPopup
          show={popupState.show}
          onHide={() => setPopupState({ show: false, district: null, blocks: [], incidentType: null })}
          districtData={popupState.district}
          incidentType={popupState.incidentType}
          blocks={popupState.blocks}
        />
      </div>
    </FullScreen>
  );
}

// ── Bar Chart ────────────────────────────────────────────────────────────
function CloudBurstBarChart({ selectedIncident, startDateStr, endDateStr, onBarClick, isTrendExpanded }) {
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading]     = useState(false);
  const barColor = getCardColor(selectedIncident);

  useEffect(() => {
    if (!selectedIncident || !startDateStr || !endDateStr) return;
    setLoading(true);
    getData(
      cloudburstchartdata
        .replace("FROMDATE", startDateStr)
        .replace("TODATE", endDateStr)
        .replace("BARTYPE", selectedIncident)
    )
      .then(r => setChartData(r || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedIncident, startDateStr, endDateStr]);

  useEffect(() => {
    if (!chartRef.current || !chartData.length) return;
    const sorted = [...chartData.filter(i => i.count > 0)].sort((a, b) => a.count - b.count);
    if (!sorted.length) return;

    const option = {
      tooltip: {
        trigger: "axis", axisPointer: { type: "shadow" },
        formatter: p => `<div style="font-weight:700;color:${C.text1};border-bottom:1px solid ${C.border};margin-bottom:6px;padding-bottom:4px">${p[0].name}</div><span style="color:${C.text3}">Count: </span><span style="font-weight:700;color:${barColor}">${p[0].value.toLocaleString("en-IN")}</span>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(12,74,110,.1);font-family:'Inter',sans-serif;",
      },
      grid: { left: "15%", right: "10%", bottom: "10%", top: "6%", containLabel: false },
      xAxis: {
        type: "value",
        axisLabel: { color: C.text3, fontSize: 11 },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "category",
        data: sorted.map(i => i.dist_name),
        axisLabel: { color: C.text1, fontSize: 12, fontWeight: "600", padding: [0, 10, 0, 0] },
        axisLine: { lineStyle: { color: C.border, width: 1 } },
      },
      series: [{
        type: "bar",
        data: sorted.map(item => ({
          value: item.count,
          itemStyle: {
            color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0,
              colorStops: [{ offset: 0, color: "#BAE6FD" }, { offset: 1, color: barColor }],
            },
            borderRadius: [0, 10, 10, 0],
          },
        })),
        barWidth: 22,
        label: { show: true, position: "right", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 11, fontWeight: "700", color: barColor },
        showBackground: true, backgroundStyle: { color: "rgba(2,132,199,.04)", borderRadius: [0, 10, 10, 0] },
      }],
      dataZoom: sorted.length > 6 ? [{
        type: "slider", yAxisIndex: 0, start: 0, end: 60, orient: "vertical",
        handleStyle: { color: barColor }, borderColor: C.border, backgroundColor: C.surface,
      }] : [],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    chartInstance.current.on("click", p => {
      if (p.componentType === "series") {
        const d = chartData.find(x => x.dist_name === p.name);
        if (d && onBarClick) onBarClick(d);
      }
    });
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [chartData, selectedIncident, isTrendExpanded]);

  if (!selectedIncident) return null;
  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Loader show={true} type="body" stack="vertical" message="Analyzing storm data..." /></div>;
  if (!chartData.length || !chartData.some(i => i.count > 0)) return (
    <div style={{ textAlign: "center", padding: 40, color: C.text3, fontSize: 13 }}>No data available for the selected period</div>
  );

  const validBars = chartData.filter(i => i.count > 0).length;
  const dynamicHeight = isTrendExpanded ? "calc(100vh - 160px)" : Math.max(150, Math.min(500, validBars * 45 + 80));

  return <div ref={chartRef} style={{ width: "100%", height: dynamicHeight }} />;
}

// ── Pictorial Bar Chart ─────────────────────────────────────────────────────
function PictorialBarChart({ cardData }) {
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const total = CARDS.reduce((s, c) => s + (cardData[c.key] || 0), 0) || 1;
    const blueShades = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0"];

    const categories = CARDS.map(cfg => formatLabel(cfg.key));
    const values = CARDS.map(cfg => cardData[cfg.key] || 0);
    const maxVal = Math.max(...values, 1);

    // Rain-drop SVG path for the pictorial symbol
    const dropPath = 'path://M10 0C10 0 0 12 0 17C0 22 4.5 26 10 26C15.5 26 20 22 20 17C20 12 10 0 10 0Z';

    const option = {
      title: {
        text: "IMPACT DISTRIBUTION", left: "center", top: 16,
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700" }
      },
      tooltip: {
        trigger: "item",
        formatter: function(p) {
          const pct = ((p.value / total) * 100).toFixed(1);
          return '<div style="padding:4px 2px"><div style="font-weight:700;color:' + C.text1 + ';margin-bottom:4px">' + p.name + '</div><div style="color:' + C.primary + ';font-weight:800;font-size:18px">' + p.value.toLocaleString("en-IN") + '</div><div style="color:' + C.text3 + ';font-size:11px">' + pct + '% of total</div></div>';
        },
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(12,74,110,.1);",
      },
      legend: {
        orient: "horizontal", bottom: 10,
        itemWidth: 10, itemHeight: 10, itemGap: 14,
        textStyle: { color: C.text3, fontSize: 10, fontWeight: "600" },
        data: categories
      },
      xAxis: {
        type: "category",
        data: categories,
        axisTick: { show: false },
        axisLine: { lineStyle: { color: C.border } },
        axisLabel: { color: C.text1, fontSize: 10, fontWeight: "600" }
      },
      yAxis: {
        type: "value", show: false,
        max: maxVal * 1.3
      },
      grid: { left: "10%", right: "10%", top: "20%", bottom: "22%", containLabel: true },
      series: [{
        type: "pictorialBar",
        symbol: dropPath,
        symbolSize: function(val) {
          var pct = val / maxVal;
          var w = Math.max(24, Math.min(50, pct * 60));
          return [w, '75%'];
        },
        symbolOffset: [0, '-10%'],
        data: values.map(function(val, idx) {
          return {
            value: val,
            name: categories[idx],
            itemStyle: {
              color: {
                type: "linear", x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0, color: blueShades[idx] + "CC" },
                  { offset: 1, color: blueShades[idx] }
                ]
              },
              shadowBlur: 10,
              shadowColor: blueShades[idx] + "55"
            }
          };
        }),
        label: {
          show: true, position: "top",
          formatter: function(p) { return p.value > 0 ? p.value.toLocaleString("en-IN") : ""; },
          fontSize: 11, fontWeight: "800", color: C.text1
        },
        emphasis: {
          itemStyle: { shadowBlur: 20, shadowColor: "rgba(0,0,0,0.2)" }
        },
        z: 10
      }]
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return <div className="cbd-chart-cell"><div ref={chartRef} style={{ width: "100%", height: "100%" }} /></div>;
}

// ── Nightingale Rose Chart ───────────────────────────────────────────────
function NightingaleRoseChart({ cardData }) {
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const totalAll = CARDS.reduce((s, c) => s + (cardData[c.key] || 0), 0) || 1;
    const seriesData = CARDS.map(cfg => ({
      name: formatLabel(cfg.key),
      value: cardData[cfg.key] || 0,
      itemStyle: {
        color: { type: "radial", x: 0.5, y: 0.5, r: 0.5,
          colorStops: [{ offset: 0, color: getCardColor(cfg.key) + "dd" }, { offset: 1, color: getCardColor(cfg.key) }],
        },
        borderColor: C.surface, borderWidth: 2,
        shadowBlur: 12, shadowColor: getCardColor(cfg.key) + "60",
      },
    }));

    const option = {
      title: {
        text: "NIGHTINGALE ROSE", left: "center", top: 8,
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700" },
        subtext: "Proportional impact by category",
        subtextStyle: { color: C.text3, fontSize: 10 },
      },
      tooltip: {
        trigger: "item",
        formatter: p => {
          const pct = ((p.value / totalAll) * 100).toFixed(1);
          return `<div style="padding:6px 8px;min-width:160px"><div style="font-weight:800;color:${C.text1};margin-bottom:5px;font-size:12px">${p.name}</div><div style="color:${getCardColor(CARDS.find(k => formatLabel(k.key) === p.name)?.key)};font-weight:800;font-size:20px;line-height:1">${p.value.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:11px;margin-top:3px">${pct}% of total</div></div>`;
        },
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(12,74,110,.1);",
      },
      legend: { orient: "horizontal", bottom: 8, left: "center", itemWidth: 10, itemHeight: 10, itemGap: 14, textStyle: { color: C.text3, fontSize: 10, fontWeight: "600" } },
      series: [{
        type: "pie", radius: ["25%", "70%"], center: ["50%", "48%"], roseType: "area", padAngle: 3,
        itemStyle: { borderRadius: 8 },
        label: { show: true, formatter: p => p.value > 0 ? p.value.toLocaleString("en-IN") : "", fontSize: 10, fontWeight: "700", color: C.text1, position: "outside" },
        labelLine: { length: 6, length2: 8, smooth: true, lineStyle: { width: 1, color: C.text3 } },
        emphasis: { scale: true, scaleSize: 5, itemStyle: { shadowBlur: 20, shadowColor: "rgba(0,0,0,0.2)" } },
        data: seriesData,
        avoidLabelOverlap: true,
      }],
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return <div className="cbd-chart-cell"><div ref={chartRef} style={{ width: "100%", height: "100%" }} /></div>;
}
// ── Scatter Chart ────────────────────────────────────────────────────────
function ScatterChart({ cardData }) {
  const chartRef      = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const total = CARDS.reduce((s, c) => s + (cardData[c.key] || 0), 0) || 1;

    const seriesData = CARDS.map((cfg, i) => {
      const val = cardData[cfg.key] || 0;
      const pct = parseFloat(((val / total) * 100).toFixed(1));
      return {
        name: formatLabel(cfg.key),
        value: [i + 1, val, pct],      // [x-position, count, percentage]
        symbolSize: Math.max(18, Math.min(70, (pct / 100) * 200 + 18)),
        itemStyle: {
          color: {
            type: "radial", x: 0.4, y: 0.35, r: 0.6,
            colorStops: [
              { offset: 0, color: getCardColor(cfg.key) + "CC" },
              { offset: 1, color: getCardColor(cfg.key) },
            ],
          },
          borderColor: C.surface,
          borderWidth: 2,
          shadowBlur: 10,
          shadowColor: getCardColor(cfg.key) + "55",
        },
      };
    });

    const option = {
      title: {
        text: "IMPACT SCATTER", left: "center", top: 8,
        textStyle: { color: C.text1, fontSize: 12, fontWeight: "700" },
        subtext: "Count vs category — bubble size = % share",
        subtextStyle: { color: C.text3, fontSize: 10 },
      },
      tooltip: {
        trigger: "item",
        formatter: p => `
          <div style="padding:6px 8px;min-width:160px">
            <div style="font-weight:800;color:${C.text1};margin-bottom:5px;font-size:12px">${p.name}</div>
            <div style="color:${getCardColor(CARDS.find(k => formatLabel(k.key) === p.name)?.key)};font-weight:800;font-size:20px;line-height:1">
              ${p.value[1].toLocaleString("en-IN")}
            </div>
            <div style="color:${C.text3};font-size:11px;margin-top:3px">${p.value[2]}% of total</div>
          </div>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(12,74,110,.1);",
      },
      grid: { left: "12%", right: "8%", bottom: "22%", top: "22%", containLabel: true },
      xAxis: {
        type: "value", min: 0, max: 5,
        axisLabel: {
          color: C.text1, fontSize: 10, fontWeight: "600",
          formatter: v => {
            const map = { 1: "Deaths", 2: "Hospitalized", 3: "Med. Payments", 4: "Ex-Gratia" };
            return map[v] || "";
          },
          interval: 0, rotate: 12,
        },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: C.border } },
      },
      yAxis: {
        type: "value",
        name: "Count", nameTextStyle: { color: C.text3, fontSize: 10 },
        axisLabel: { color: C.text3, fontSize: 10 },
        splitLine: { lineStyle: { color: C.border, type: "dashed" } },
        axisTick: { show: false },
      },
      series: [{
        type: "scatter",
        data: seriesData,
        label: {
          show: true, position: "top",
          formatter: p => p.value[1] > 0 ? p.value[1].toLocaleString("en-IN") : "",
          fontSize: 10, fontWeight: "700", color: C.text1,
        },
        emphasis: {
          scale: true,
          itemStyle: { shadowBlur: 20, shadowColor: "rgba(0,0,0,0.2)" },
        },
      }],
      legend: {
        orient: "horizontal", bottom: 4, left: "center",
        itemWidth: 10, itemHeight: 10, itemGap: 14,
        textStyle: { color: C.text3, fontSize: 10, fontWeight: "600" },
        data: CARDS.map((cfg, i) => ({
          name: formatLabel(cfg.key),
          icon: "circle",
          itemStyle: { color: getCardColor(cfg.key) },
        })),
      },
    };

    if (chartInstance.current) { try { chartInstance.current.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; }
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(option);
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); try { chartInstance.current?.dispose(); } catch (_) { /* tooltip DOM race */ } chartInstance.current = null; };
  }, [cardData]);

  return <div className="cbd-chart-cell"><div ref={chartRef} style={{ width: "100%", height: "100%" }} /></div>;
}